/**
 * Celestial Movement Calculations
 * 
 * Calculates 3D movement of stars and the user's galactic journey
 * using Gaia DR3 data and standard astronomical formulas.
 */

/**
 * Calculate 3D movement of a star since user's birth
 * 
 * @param {Object} star - Star object with pmra, pmdec, distance_ly, radial_velocity
 * @param {number} birthYear - User's birth year
 * @returns {Object|null} Movement data or null if insufficient data
 */
export function calculate3DMovement(star, birthYear) {
  const currentYear = new Date().getFullYear();
  const yearsElapsed = currentYear - birthYear;
  
  // Check if we have minimum required data
  if (!star.pmra || !star.pmdec || !star.distance_ly) {
    return null;
  }
  
  // Convert proper motion from mas/yr to arcsec/yr
  const pmra_arcsec = star.pmra / 1000;
  const pmdec_arcsec = star.pmdec / 1000;
  
  // Total proper motion (Pythagorean theorem)
  const total_pm_arcsec = Math.sqrt(pmra_arcsec ** 2 + pmdec_arcsec ** 2);
  
  // Angular displacement since birth (arcseconds)
  const angular_drift_arcsec = total_pm_arcsec * yearsElapsed;
  
  // Convert distance from light-years to parsecs (1 ly = 0.306601 pc)
  const distance_pc = star.distance_ly * 0.306601;
  
  // Transverse velocity in km/s
  // Formula: V_transverse = 4.74 × μ (arcsec/yr) × d (parsecs)
  const v_transverse = 4.74 * total_pm_arcsec * distance_pc;
  
  // Time elapsed in seconds
  const seconds_elapsed = yearsElapsed * 31557600; // seconds per year
  
  // Physical distance traveled (transverse/2D only)
  const distance_transverse_km = v_transverse * seconds_elapsed;
  
  let v_total = v_transverse;
  let distance_total_km = distance_transverse_km;
  let has_3d = false;
  let distance_radial_km = 0;
  
  // If radial velocity available, calculate true 3D
  if (star.radial_velocity != null && !isNaN(star.radial_velocity)) {
    const v_radial = Math.abs(star.radial_velocity); // km/s
    
    // Total 3D velocity (Pythagorean theorem in 3D)
    v_total = Math.sqrt(v_transverse ** 2 + v_radial ** 2);
    
    // Physical distance traveled in 3D
    distance_total_km = v_total * seconds_elapsed;
    distance_radial_km = v_radial * seconds_elapsed;
    
    has_3d = true;
  }
  
  return {
    // Angular measurements
    angular_drift_arcsec: angular_drift_arcsec.toFixed(2),
    
    // Distances in billion km
    distance_transverse_billion_km: (distance_transverse_km / 1e9).toFixed(2),
    distance_radial_billion_km: (distance_radial_km / 1e9).toFixed(2),
    distance_total_billion_km: (distance_total_km / 1e9).toFixed(2),
    
    // Velocities
    velocity_transverse_kms: v_transverse.toFixed(2),
    velocity_total_kms: v_total.toFixed(2),
    
    // Metadata
    has_3d_data: has_3d,
    years_elapsed: yearsElapsed,
    birth_year: birthYear,
    current_year: currentYear,
    
    // User-friendly comparisons
    solar_system_widths: (distance_total_km / 1.2e10).toFixed(1), // Solar system diameter ~12 billion km
    earth_sun_trips: (distance_total_km / 1.496e8).toFixed(0) // 1 AU = 149.6 million km
  };
}

/**
 * Calculate user's galactic journey through the Milky Way
 * 
 * @param {number} birthYear - User's birth year
 * @returns {Object} Galactic journey data
 */
const SECONDS_PER_YEAR = 31557600;
const KM_PER_LIGHT_SECOND = 299792.458;
const KM_PER_LIGHT_YEAR = 9.4607e12;

/** Distance in km, at whatever magnitude actually reads. */
function formatJourneyDistance(km) {
  if (km >= 1e9) return { value: (km / 1e9).toFixed(1), unit: 'billion km' };
  if (km >= 1e6) return { value: (km / 1e6).toFixed(1), unit: 'million km' };
  if (km >= 1e3) return { value: Math.round(km).toLocaleString(), unit: 'km' };
  return { value: km.toFixed(0), unit: 'km' };
}

/**
 * The same distance expressed as light-travel time. Quoting light-years for a
 * newborn gives 0.000000, which tells the reader nothing — so this steps down
 * through days, hours and minutes as the distance shrinks.
 */
function formatLightTime(km) {
  if (km >= KM_PER_LIGHT_YEAR / 100) return `${(km / KM_PER_LIGHT_YEAR).toFixed(6)} light-years`;
  const ls = km / KM_PER_LIGHT_SECOND;
  if (ls >= 86400) return `${(ls / 86400).toFixed(1)} light-days`;
  if (ls >= 3600) return `${(ls / 3600).toFixed(1)} light-hours`;
  if (ls >= 60) return `${(ls / 60).toFixed(1)} light-minutes`;
  return `${ls.toFixed(1)} light-seconds`;
}

/** Age in whichever unit is meaningful — "0 years" is not an age. */
function formatAge(seconds) {
  const days = seconds / 86400;
  if (days >= 730) return { value: Math.floor(days / 365.2425), unit: 'years' };
  if (days >= 60) return { value: Math.floor(days / 30.44), unit: 'months' };
  if (days >= 1) return { value: Math.floor(days), unit: days < 2 ? 'day' : 'days' };
  return { value: Math.max(0, Math.floor(seconds / 3600)), unit: 'hours' };
}

/**
 * Calculate the user's galactic journey through the Milky Way.
 *
 * Takes the full birth DATE, not just the year. The previous version used
 * `currentYear - birthYear`, which made anyone born in the current year exactly
 * zero years old and therefore zero km travelled, and gave a January and a
 * December birth in the same year identical results.
 *
 * @param {Date|string|number} birthInput - birth Date, ISO string, or year
 */
export function calculateGalacticJourney(birthInput) {
  let birth;
  if (birthInput instanceof Date) birth = birthInput;
  else if (typeof birthInput === 'number') birth = new Date(birthInput, 0, 1); // legacy: bare year
  else birth = new Date(birthInput);

  const now = new Date();
  const elapsedSeconds = Math.max(0, (now.getTime() - birth.getTime()) / 1000);
  const ageYears = elapsedSeconds / SECONDS_PER_YEAR;

  // The solar system's motion around the galactic centre.
  const solar_velocity_kms = 230;
  const distance_km = solar_velocity_kms * elapsedSeconds;

  const dist = formatJourneyDistance(distance_km);
  const age = formatAge(elapsedSeconds);

  return {
    age: age.value,
    age_unit: age.unit,
    age_years_exact: ageYears,
    distance_km: distance_km,

    // Adaptive display values.
    distance_value: dist.value,
    distance_unit: dist.unit,
    light_time: formatLightTime(distance_km),

    // Kept so existing callers don't break; now derived from the real elapsed time.
    distance_billion_km: (distance_km / 1e9).toFixed(1),
    distance_light_years: (distance_km / KM_PER_LIGHT_YEAR).toFixed(6),

    velocity_kms: solar_velocity_kms,
    velocity_mph: (solar_velocity_kms * 0.621371).toFixed(0),
    birth_year: birth.getFullYear(),
    current_year: now.getFullYear(),

    earth_circumferences: (distance_km / 40075).toFixed(0),
    neptune_orbits: (distance_km / 2.82e10).toFixed(1) // Neptune orbit distance
  };
}

/**
 * Format large numbers with appropriate units
 * 
 * @param {number} billionKm - Distance in billion km
 * @returns {string} Formatted string
 */
export function formatCosmicDistance(billionKm) {
  const value = parseFloat(billionKm);
  
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} trillion km`;
  } else if (value >= 1) {
    return `${value.toFixed(1)} billion km`;
  } else {
    return `${(value * 1000).toFixed(0)} million km`;
  }
}

/**
 * Get user-friendly description of star movement
 * 
 * @param {Object} movement - Movement data from calculate3DMovement
 * @returns {string} Description
 */
export function getMovementDescription(movement) {
  if (!movement) {
    return "Movement data not available";
  }
  
  if (movement.has_3d_data) {
    return `This star has traveled ${formatCosmicDistance(movement.distance_total_billion_km)} through the galaxy since your birth, combining sideways motion (${formatCosmicDistance(movement.distance_transverse_billion_km)}) and radial motion (${formatCosmicDistance(movement.distance_radial_billion_km)}).`;
  } else {
    return `This star has traveled at least ${formatCosmicDistance(movement.distance_transverse_billion_km)} sideways through the galaxy since your birth. (Radial velocity data not available for 3D calculation.)`;
  }
}
